import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, finalize, of, Subscription, take, tap } from 'rxjs';
import { ILocationArchiveData } from '../../models/location/location-archive-data.model';
import { ILocationPostData } from '../../models/location/location-post-data.model';
import { ILocationUpdateData } from '../../models/location/location-update-data.model';
import { ILocation } from '../../models/location/location.model';
import { LocationsService } from '../entities/locations/locations.service';
import { SelectedOrgService } from '../selected-org.service';
import { UtilsService } from '../utils.service';

export interface IOrgLocationsState {
  locations$: BehaviorSubject<ILocation[]>;
  fetchingLocations$: BehaviorSubject<boolean>;
  updatingLocations$: BehaviorSubject<number[]>;

};

interface IInitData {
  orgId: number;
}

@Injectable({
  providedIn: 'root'
})
export class OrgLocationsStoreService implements OnDestroy {

  private initState: IOrgLocationsState = {
    locations$: new BehaviorSubject<ILocation[]>([]),
    fetchingLocations$: new BehaviorSubject<boolean>(false),
    updatingLocations$: new BehaviorSubject<number[]>([]),
  };
  private state: IOrgLocationsState = this.utilsService.initializeState(this.initState) as IOrgLocationsState;

  public inited = false;
  initData: IInitData | undefined;
  private subs: Subscription[] = [];

  constructor(
    private selectedOrgService: SelectedOrgService,
    private locationsService: LocationsService,
    private utilsService: UtilsService
  ) {
    this.subs.push(

    );
  }

  

  public init() {
    this.inited = true;
    this.reset();

    const orgId = this.selectedOrgService.getCurrentValue();
    if (!orgId) return;
    this.initData = { orgId };

    this.state.fetchingLocations$.next(true);
    this.getLocations(this.initData).pipe(
      take(1),
      finalize(() => this.state.fetchingLocations$.next(false))
    ).subscribe({
      next: (res) => {
        this.state.locations$.next(res);
      }
    });
  }

  private getLocations(initData: IInitData) {
    return this.locationsService.getLocationsForOrgLocationsStore(initData.orgId).pipe(
      take(1),
      tap(x => {

      })
    );
  }

  public createLocation(data: ILocationPostData) {
    return this.locationsService.postLocation(data).pipe(
      take(1),
      tap(x => {
        this.init();
      })
    );
  }

  public updateLocation(data: ILocationUpdateData) {
    this.state.updatingLocations$.next([ ...this.state.updatingLocations$.getValue(), data.id ]);
    return this.locationsService.updateLocation(data).pipe(
      take(1),
      finalize(() => this.state.updatingLocations$.next(this.state.updatingLocations$.getValue().filter(x => x !== data.id))),
      tap(x => {
        this.updateLocationsInState([data.id]);
      })
    );
  }

  public deleteLocation(data: ILocationArchiveData) {
    this.state.updatingLocations$.next([ ...this.state.updatingLocations$.getValue(), data.id ]);
    return this.locationsService.deleteLocation(data).pipe(
      take(1),
      finalize(() => this.state.updatingLocations$.next(this.state.updatingLocations$.getValue().filter(x => x !== data.id))),
      tap(x => {
        const currLocations = this.state.locations$.getValue();
        const locIndexToDelete = currLocations.findIndex(l => l.id === data.id);
        if (locIndexToDelete >= 0) {
          currLocations.splice(locIndexToDelete, 1);
          this.state.locations$.next(currLocations);
        }
      })
    )
  }

  public updateLocationsInState(locationIds: number[]) {
      if (!this.initData) return;
      this.addUpdatingToState(locationIds);
      this.getLocations(this.initData).pipe(
        take(1),
        finalize(() => this.removeUpdatingFromState(locationIds)),
      ).subscribe({
        next: (res) => {
          const result = res;
  
          const updatedReservations = result.filter(x => locationIds.find(y => x.id === y));
          const currLocations = [...this.state.locations$.getValue()];
  
          // update undirectly changed reservations (by state and order.state)
          for (let i = 0; i < currLocations.length; i++) {
            const currReservation = currLocations[i];
            const index = result.findIndex(x => x.id === currReservation.id);
            if (index >= 0) {
              currLocations[i] = result[index];
            }
          }
  
          // update directly changed reservations in state
          for (let updatedReservation of updatedReservations) {
            const index = currLocations.findIndex(x => x.id === updatedReservation.id); 
            if (index >= 0) {
              currLocations[index] = updatedReservation;
            }
          }
          this.state.locations$.next(currLocations);
        }
      });
    }

  private addUpdatingToState(ids: number[]) {
    this.state.updatingLocations$.next( [ ...this.state.updatingLocations$.getValue(), ...ids ] );
  }
  private removeUpdatingFromState(ids: number[]) {
    this.state.updatingLocations$.next( this.state.updatingLocations$.getValue().filter(x => !ids.includes(x)) );
  }

  private reset() {
    this.utilsService.resetState(this.initState, this.state);
  }

  public getState() {
    return this.state;
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }
}
